import { Link, usePage } from '@inertiajs/react'
import { ClipboardList, AlertCircle } from 'lucide-react'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { SearchInput } from '@/components/shared/SearchInput'
import { Pagination, PaginationMeta } from '@/components/shared/Pagination'
import { BusinessTaskSummary, DashboardPageProps } from '@/types'
import { StatusBadge } from '@/components/shared/StatusBadge'

type Props = DashboardPageProps & {
  tasks: BusinessTaskSummary[]
  search: string
  pagination: PaginationMeta
}

export default function BusinessTasksIndex() {
  const { tasks, search, pagination, auth } = usePage<Props>().props
  const slug = auth.account?.slug

  return (
    <DashboardLayout title="Tasks">
      <div className="mb-8">
        <h1 className="text-xl font-semibold tracking-tight">Tasks</h1>
        <p className="text-sm text-muted-foreground">{pagination.total} assigned {pagination.total === 1 ? 'task' : 'tasks'}</p>
      </div>

      <SearchInput value={search} placeholder="Search tasks..." />

      <div className="mt-4">
        {tasks.length === 0 ? (
          <div className="border rounded-lg py-16 text-center">
            <ClipboardList className="w-6 h-6 mx-auto mb-3 text-muted-foreground/30" />
            <h3 className="text-sm font-medium text-foreground mb-1">No tasks assigned yet</h3>
            <p className="text-xs text-muted-foreground">Tasks assigned by admin will appear here.</p>
          </div>
        ) : (
          <div className="border rounded-lg">
            {tasks.map((task, i) => (
              <Link
                key={task.id}
                href={`/${slug}/business_tasks/${task.id}`}
                className={`flex items-center justify-between px-4 py-3 hover:bg-muted/50 transition-colors ${i > 0 ? 'border-t' : ''}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center shrink-0">
                    <ClipboardList className="w-4 h-4 text-muted-foreground" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-foreground truncate">{task.type_name}</span>
                      <StatusBadge status={task.status} />
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <span>{task.business_name}</span>
                      {task.due_date && (
                        <>
                          <span>·</span>
                          <span className={task.overdue ? 'text-red-600 font-medium flex items-center gap-1' : ''}>
                            {task.overdue && <AlertCircle className="w-3 h-3" />}
                            Due: {new Date(task.due_date).toLocaleDateString()}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      <div className="mt-4">
        <Pagination pagination={pagination} />
      </div>
    </DashboardLayout>
  )
}
